import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";

const Campaigns = () => {
  const campaigns = [
    {
      id: "thousand-smiles",
      image: "/10thdecember/img1.jpeg",
      title: "A Thousand Smiles - IDP Camp Feeding Initiative",
      date: "December 10, 2025",
      category: "Community Outreach",
      excerpt: "A successful feeding initiative at Area 1 IDP Camp, Abuja. We brought smiles, food, clothing, and essential supplies to displaced families, making a real difference in their lives.",
    },
    {
      id: "lee-royalty-orphanage",
      image: "/leeroyalty/img1.jpg",
      title: "Lee Royalty Orphanage Center Outreach",
      date: "February 22, 2026",
      category: "Orphanage Outreach",
      excerpt: "A day of love, hope, and meaningful impact at Lee Royalty Orphanage Center, Benin City, Edo State. We shared support, essentials, and reminded these incredible children that they are seen, valued, and deeply loved.",
    },
    {
      id: "charity-faith-hope",
      image: "/charityfaith/img1.jpeg",
      title: "Charity Faith and Hope Orphanage Visit",
      date: "April 15, 2025",
      category: "Orphanage Outreach",
      excerpt: "Our team had the privilege of visiting Charity Faith and Hope Orphanage Home to spend quality time with the children and share love. We played games, told stories, and listened to their dreams.",
    },
    {
      id: "gods-pavilion",
      image: "/gallery/img7.jpeg",
      title: "God's Pavilion Orphanage Outreach",
      date: "April 22, 2025",
      category: "Orphanage Outreach",
      excerpt: "A heartwarming day at God's Pavilion Orphanage Home at Apo Waru, Abuja-FCT. We laughed, played games, shared stories, and listened to the dreams of these amazing children.",
    },
    {
      id: "national-hospital",
      image: "/may8th/img1.jpeg",
      title: "National Hospital Abuja Medical Support",
      date: "May 8, 2025",
      category: "Healthcare",
      excerpt: "We visited National Hospital Abuja where we paid several medical bills for patients and gave out essential items to the sick. A day filled with compassion, connection, and care.",
    },
    {
      id: "shoes-for-all",
      image: "/shoesforall/img1.jpeg",
      title: "Shoes For All - Footwear Making Workshop",
      date: "2025",
      category: "Skills Training",
      excerpt: "In collaboration with @thegirlchildvsi and @its_feyishayo, we launched a 3-day Footwear-Making Workshop training young women in hands-on shoemaking skills.",
    },
    {
      id: "police-appreciation",
      image: "/policeappreciation/img1.jpeg",
      title: "Police Appreciation Outreach",
      date: "August 21, 2025",
      category: "Community Outreach",
      excerpt: "We visited the Durumi Police Division, Abuja to honor the brave men and women who dedicate their lives to keeping us safe. Building bridges of friendship and understanding.",
    },
    {
      id: "nisa-hospital",
      image: "/nisa/img1.jpeg",
      title: "Nisa Garki Hospital Outreach",
      date: "November 15, 2025",
      category: "Healthcare",
      excerpt: "A day of giving, healing, and true human connection at Nisa Garki Hospital. We supported families by paying medical bills and shared heartfelt words of encouragement.",
    },
  ];

  const categories = ["All", "Orphanage Outreach", "Healthcare", "Skills Training", "Community Outreach"];
  const [activeCategory, setActiveCategory] = React.useState("All");

  const filteredCampaigns = activeCategory === "All"
    ? campaigns
    : campaigns.filter(c => c.category === activeCategory);

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-cream py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-primary font-medium text-sm tracking-wider uppercase mb-3">OUR WORK</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Our Campaigns
            </h1>
            <p className="text-gray-600 text-lg">
              Discover how we're making a difference in communities across Nigeria through our various outreach programs and initiatives.
            </p>
          </div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${activeCategory === category
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Campaigns Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCampaigns.map((campaign, index) => (
              <Link
                key={index}
                to={`/campaigns/${campaign.id}`}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={campaign.image}
                    alt={campaign.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4 text-white text-xs font-medium px-3 py-1 rounded-full bg-primary">
                    {campaign.category}
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center text-gray-500 text-sm mb-3">
                    <Calendar className="w-4 h-4 mr-2" />
                    {campaign.date}
                  </div>
                  <h3 className="font-semibold text-xl text-gray-900 mb-3 group-hover:text-primary transition-colors">
                    {campaign.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">
                    {campaign.excerpt}
                  </p>
                  <span className="text-primary font-medium text-sm inline-flex items-center gap-2">
                    Read More <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-cream">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Want to Support Our Campaigns?
          </h2>
          <p className="text-gray-600 text-lg mb-8">
            Your donation helps us continue making a difference in the lives of those who need it most.
          </p>
          <Link
            to="/donate"
            className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-full font-medium text-sm transition-colors inline-flex items-center gap-2"
          >
            DONATE NOW <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Campaigns;
